import { useLocalization } from "@fluent/react";
import { TextInput as NDSTextInput } from "@narmi/design_system";
import { Control, Controller, FieldPath } from "react-hook-form";
import styles from "./AnnualMonthlyIncome.module.css";
import { useMonthlyIncomeCalculator } from "./hooks/useMonthlyIncomeCalculator";
import { FormWithAmount } from "./types";
import type { ChangeEvent, ClipboardEvent, KeyboardEvent } from "react";

interface Props<T extends FormWithAmount> {
  control: Control<T>;
}
function AnnualMonthlyIncome<T extends FormWithAmount>({ control }: Props<T>) {
  const { l10n } = useLocalization();

  const monthlyIncome = useMonthlyIncomeCalculator(control);
  const annualIncomeLabel = l10n.getString("add-income-annual-income", null, "Annual income");
  const monthlyIncomeLabel = l10n.getString("add-income-monthly-income", null, "Monthly income");

  function formatWholeDollarValue(value: string | number | undefined) {
    if (typeof value === "number") {
      return `$${value.toLocaleString()}`;
    }

    if (!value || value === "$") {
      return value ?? "";
    }

    const numericValue = String(value).replace(/[$,]/g, "");

    if (!numericValue) {
      return "$";
    }

    return `$${parseInt(numericValue, 10).toLocaleString()}`;
  }

  return (
    <div className={styles.container}>
      <Controller
        name={"amount" as FieldPath<T>}
        control={control}
        render={({ field, fieldState }) => {
          const fieldValue = field.value as string | number | undefined;

          const getDigitsBeforeDecimal = (value: string) => {
            const [wholeDollars] = value.split(".");
            return wholeDollars.replace(/[^\d]/g, "");
          };

          const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
            const rawValue = getDigitsBeforeDecimal(event.target.value);

            if (!rawValue) {
              field.onChange("$");
              return;
            }

            field.onChange(rawValue);
          };

          const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === ".") {
              event.preventDefault();
            }
          };

          const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
            const pastedValue = event.clipboardData.getData("text");

            if (!pastedValue.includes(".")) {
              return;
            }

            event.preventDefault();

            const rawValue = getDigitsBeforeDecimal(pastedValue);
            field.onChange(rawValue || "$");
          };

          const handleFocus = () => {
            if (!fieldValue) {
              field.onChange("$");
            } else if (typeof fieldValue === "number") {
              field.onChange(fieldValue.toString());
            }
          };

          const handleBlur = () => {
            if (fieldValue === "$") {
              field.onChange("");
              field.onBlur();
              return;
            }

            const numericValue = String(fieldValue ?? "").replace(/[$,]/g, "");
            field.onChange(numericValue ? parseInt(numericValue, 10) : undefined);
            field.onBlur();
          };

          return (
            <NDSTextInput
              label={annualIncomeLabel}
              onChange={handleChange}
              onBlur={handleBlur}
              onFocus={handleFocus}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              value={formatWholeDollarValue(fieldValue)}
              error={fieldState.error?.message}
              inputMode="numeric"
            />
          );
        }}
      />
      <NDSTextInput label={monthlyIncomeLabel} disabled value={monthlyIncome} />
    </div>
  );
}

export { AnnualMonthlyIncome };
